
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Wand2, Image as ImageIcon, Save, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

const ImageGenerator = () => {
  const [imagePrompt, setImagePrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const dataURLToBlob = async (dataURL: string): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get 2D context'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          }, 'image/png');
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image from data URL'));
        };
        
        img.src = dataURL;
      } catch (error) {
        reject(error);
      }
    });
  };

  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt for the image",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-image', {
        body: { prompt: imagePrompt }
      });

      if (error) throw error;
      if (!data?.image) throw new Error('No image data received from the server');

      setGeneratedImage(data.image);
      toast({
        title: "Success",
        description: "Image generated successfully.",
      });
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate image",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      setGeneratedImage(publicUrl);
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const saveImageToGallery = async () => {
    if (!generatedImage) {
      toast({
        title: "Error",
        description: "No image to save",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      // Check if the image is a data URL or a regular URL
      if (generatedImage.startsWith('data:')) {
        // Convert data URL to blob
        const blob = await dataURLToBlob(generatedImage);
        const fileName = `studio-generated-${Date.now()}.png`;
        
        const { error: uploadError } = await supabase.storage
          .from('article-images')
          .upload(fileName, blob, {
            contentType: 'image/png'
          });

        if (uploadError) throw uploadError;

        toast({
          title: "Success",
          description: "Image saved to gallery successfully",
        });
      } else {
        // If it's already a URL (not a data URL), it's already in the gallery
        toast({
          title: "Success",
          description: "Image is already in the gallery",
        });
      }
    } catch (error) {
      console.error("Error saving to gallery:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save to gallery",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="generate">
              <TabsList className="mb-4">
                <TabsTrigger value="generate">Generate Image</TabsTrigger>
                <TabsTrigger value="upload">Upload Image</TabsTrigger>
              </TabsList>
              
              <TabsContent value="generate" className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Image Description</label>
                  <Textarea
                    placeholder="Describe the image you want to generate, e.g., 'A Scottish potato field with mountains in the background during sunset'"
                    value={imagePrompt}
                    onChange={(e) => setImagePrompt(e.target.value)}
                    disabled={isGenerating}
                    className="min-h-[120px] resize-none"
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={generateImage} 
                    disabled={isGenerating || !imagePrompt.trim()}
                  >
                    <Wand2 className="mr-2 h-4 w-4" />
                    {isGenerating ? "Generating..." : "Generate Image"}
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="upload" className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Upload an Image</label>
                  <p className="text-sm text-gray-500">
                    Upload an image from your computer to work with.
                  </p>
                </div>
                
                <div className="flex items-center justify-center w-full">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 text-gray-500 mb-2" />
                      <p className="text-sm text-gray-500">
                        Click to upload or drag and drop
                      </p>
                    </div>
                    <Input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-4">Image Preview</h2>
            
            {generatedImage ? (
              <div className="space-y-4">
                <div className="aspect-square relative rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={generatedImage}
                    alt="Generated or uploaded image"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={saveImageToGallery}
                    disabled={isUploading}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save to Gallery
                  </Button>
                </div>
              </div>
            ) : (
              <div className="aspect-square flex items-center justify-center bg-gray-100 rounded-lg">
                <div className="text-center text-gray-500">
                  <ImageIcon className="mx-auto h-12 w-12 mb-2" />
                  <p>No image generated yet</p>
                  <p className="text-sm">Generate or upload an image to see it here</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImageGenerator;
